// src/features/places/MarketsMapView.jsx
//
// PLACES-SLICE-2: Markets lens Map view. One pin per city (market), clustered
// by the shared OfficeMapCanvas. Pin positions follow the deterministic
// geography rule in marketInventory.js; markets without any valid member
// coordinate are listed as "Not on map" instead of being given a guessed pin.

import React, { useMemo, useRef } from "react";
import { T } from "../../app/tokens.js";
import { OfficeMapCanvas } from "../locations/OfficeMapFoundation.jsx";
import { SPACE_FIT_HEX, SPACE_FIT_VERDICT_OVER, SPACE_FIT_VERDICT_RIGHT } from "../../utils/spaceFitVerdict.js";
import { countedMarketOffices, marketGeography } from "./marketInventory.js";
import { peopleSeatsLabel } from "./MarketTilesView.jsx";
import { PartialMarker } from "../../components/PartialTotal.jsx";
import { marketAssignedToday, marketInstalledToday } from "../../services/seatAccounting.js";

export function buildMarketMapFeatures(markets, locations) {
  const mapped = [];
  const unmapped = [];
  for (const m of markets) {
    const members = countedMarketOffices(locations, m.id);
    const geo = marketGeography(members);
    if (!geo) { unmapped.push({ market: m, members }); continue; }
    const assigned = marketAssignedToday(m), seats = marketInstalledToday(m);
    const hasData = assigned != null && seats != null;
    const over = hasData && assigned > seats;
    const severity = !hasData ? 5 : over ? 0 : 3;
    mapped.push({
      key: `market:${m.id}`,
      coordinates: geo.coordinates,
      locations: [{ id: m.id, market: m.name ?? m.id }],
      color: !hasData ? T.mid : SPACE_FIT_HEX[over ? SPACE_FIT_VERDICT_OVER : SPACE_FIT_VERDICT_RIGHT],
      severity,
      market: m,
      members,
    });
  }
  return { mapped, unmapped };
}

function MarketPopup({ feature, onOpenMarket }) {
  const m = feature.market;
  return (
    <div style={{ font: "inherit", minWidth: 190 }}>
      <div style={{ fontWeight: T.fwBold, fontSize: T.fsBase, color: T.dark }}>{m.name}</div>
      <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 2 }}>{m.region} · {feature.members.length} offices</div>
      <div style={{ fontSize: T.fsXs, color: T.dark, marginTop: 6 }}>Assigned / seats: {peopleSeatsLabel(m)}<PartialMarker coverage={m.assignmentToday?.assigned} measure="assignment" /><PartialMarker coverage={m.assignmentToday?.seats} measure="installed seats" /></div>
      <button type="button" data-testid={`map-open-market-${m.id}`} onClick={() => onOpenMarket(m.id)}
        style={{ marginTop: 8, border: 0, background: "none", padding: 0, color: T.blue, fontWeight: T.fwBold, cursor: "pointer" }}>
        Open market ›
      </button>
    </div>
  );
}

export function MarketsMapView({ markets, locations, loading, error, onRetry, onOpenMarket }) {
  const mapRef = useRef(null);
  const { mapped, unmapped } = useMemo(() => buildMarketMapFeatures(markets, locations), [markets, locations]);
  const onOpenRef = useRef(onOpenMarket);
  onOpenRef.current = onOpenMarket;

  if (loading) {
    return <div data-testid="markets-map-loading" style={{ height: 560, borderRadius: 12, background: "var(--v2-hair)" }} />;
  }
  if (error) {
    return (
      <div role="alert" data-testid="markets-map-error" style={{ padding: 20, color: T.ragRed, fontSize: T.fsSm }}>
        Office locations could not be loaded, so market pins are unavailable. {error}
        {onRetry && <button type="button" onClick={onRetry} style={{ marginLeft: 10, color: T.blue, background: "none", border: 0, fontWeight: T.fwBold, cursor: "pointer" }}>Retry</button>}
      </div>
    );
  }
  return (
    <div data-testid="markets-map" style={{ display: "flex", height: 560, borderRadius: 12, overflow: "hidden", border: `1px solid ${T.border}`, boxShadow: T.cardShadow, isolation: "isolate", background: T.card }}>
      <aside data-testid="markets-map-sidebar" style={{ width: 280, flexShrink: 0, borderRight: `1px solid ${T.border}`, overflowY: "auto" }}>
        <div style={{ padding: "10px 14px", fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase", color: T.mid, borderBottom: "1px solid var(--v2-hair)" }}>
          {mapped.length} on map
        </div>
        {mapped.map((f) => (
          <button key={f.key} type="button" data-testid={`markets-map-row-${f.market.id}`} onClick={() => onOpenMarket(f.market.id)}
            className="places-office-row"
            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "10px 14px", border: 0, borderBottom: "1px solid var(--v2-hair)", background: "transparent", font: "inherit", cursor: "pointer" }}>
            <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: "50%", background: f.color, flexShrink: 0 }} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontWeight: T.fwBold, fontSize: T.fsSm, color: T.dark }}>{f.market.name}</span>
              <span style={{ display: "block", fontSize: T.fsXs, color: T.mid }}>{f.members.length} offices · {peopleSeatsLabel(f.market)}</span>
            </span>
          </button>
        ))}
        {unmapped.length > 0 && (
          <div data-testid="markets-map-unmapped">
            <div style={{ padding: "10px 14px", fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase", color: T.mid, background: "var(--v2-tint)" }}>
              Not on map · no office coordinates
            </div>
            {unmapped.map(({ market }) => (
              <button key={market.id} type="button" data-testid={`markets-map-unmapped-${market.id}`} onClick={() => onOpenMarket(market.id)}
                className="places-office-row"
                style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", border: 0, borderBottom: "1px solid var(--v2-hair)", background: "transparent", font: "inherit", fontSize: T.fsSm, color: T.mid, cursor: "pointer" }}>
                {market.name}
              </button>
            ))}
          </div>
        )}
      </aside>
      <div style={{ flex: 1, position: "relative" }}>
        <OfficeMapCanvas
          features={mapped}
          mapRef={mapRef}
          cluster
          testId="markets-map-canvas"
          ariaLabel="Markets map"
          onSelectFeature={(feature) => feature.market && onOpenRef.current(feature.market.id)}
          renderPopup={(feature) => feature.market
            ? <MarketPopup feature={feature} onOpenMarket={(id) => onOpenRef.current(id)} />
            : null}
        />
      </div>
    </div>
  );
}
